import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StarRating, Loading, EmptyState } from "@/components/shared";
import { Search, UserSearch, Building2, MapPin, ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Recruiters() {
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [type, setType] = useState(params.get("type") || "all");
  const [market, setMarket] = useState("all");
  const [sort, setSort] = useState("rating");
  const [recruiters, setRecruiters] = useState(null);
  const [markets, setMarkets] = useState([]);

  const load = (o = {}) => {
    setRecruiters(null);
    api.get("/recruiters", { params: {
      search: o.search ?? q,
      type: (o.type ?? type) === "all" ? "" : (o.type ?? type),
      market: (o.market ?? market) === "all" ? "" : (o.market ?? market),
      sort: o.sort ?? sort,
    } }).then(({ data }) => setRecruiters(data)).catch(() => setRecruiters([]));
  };

  useEffect(() => {
    load();
    api.get("/markets").then(({ data }) => setMarkets(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  return (
    <main className="mx-auto max-w-[1800px] px-6 py-12 sm:px-10 lg:px-16">
      <p className="eyebrow mb-3">Recruiters</p>
      <h1 className="text-4xl font-bold">Rate the people who place you</h1>
      <p className="mt-3 max-w-4xl text-slate-400">
        Agencies and individual recruiters, scored by candidates on honesty, communication, salary
        transparency and whether they asked before sending your CV.
      </p>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <form onSubmit={(e) => { e.preventDefault(); load({ search: q }); }} className="flex flex-1 items-center gap-2 rounded-full border border-[#DDDAD1] bg-ink-900 p-1.5">
          <Search className="ml-3 h-5 w-5 text-slate-400" />
          <input data-testid="recruiter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recruiters or agencies…" className="min-w-0 flex-1 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-slate-500" />
          <button className="btn-teal rounded-full text-sm">Search</button>
        </form>
        <Select value={sort} onValueChange={(v) => { setSort(v); load({ sort: v }); }}>
          <SelectTrigger data-testid="recruiter-sort" className="w-full border-[#DDDAD1] bg-ink-900 sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="rating">Highest rated</SelectItem>
            <SelectItem value="reviews">Most reviewed</SelectItem>
            <SelectItem value="name">Name (A-Z)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <Select value={type} onValueChange={(v) => { setType(v); load({ type: v }); }}>
          <SelectTrigger data-testid="recruiter-type" className="w-44 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="agency">Agencies</SelectItem>
            <SelectItem value="individual">Individuals</SelectItem>
          </SelectContent>
        </Select>
        <Select value={market} onValueChange={(v) => { setMarket(v); load({ market: v }); }}>
          <SelectTrigger data-testid="recruiter-market" className="w-48 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Hub" /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="all">All hubs</SelectItem>
            {markets.map((m) => <SelectItem key={m.market} value={m.market}>{m.market}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {recruiters === null ? (
        <Loading label="Loading recruiters" />
      ) : recruiters.length === 0 ? (
        <div className="mt-10"><EmptyState icon={UserSearch} title="No recruiters found" subtitle="Try a different search or hub." /></div>
      ) : (
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {recruiters.map((r, i) => (
            <Link key={r.id} to={`/recruiter/${r.id}`} data-testid="recruiter-card" className="card-surface lift flex flex-col p-6 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-ink">{r.name}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                    {r.type === "agency" ? <Building2 className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                    {r.type === "agency" ? "Agency" : "Individual recruiter"}
                  </p>
                </div>
                <span className="font-heading text-3xl font-bold text-teal-400">{r.avg_overall || "-"}</span>
              </div>
              <p className="mt-3 line-clamp-2 text-sm text-slate-400">{r.about}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(r.markets || []).slice(0, 3).map((m) => (
                  <span key={m} className="inline-flex items-center gap-1 rounded-full border border-[#DDDAD1] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-slate-400"><MapPin className="h-2.5 w-2.5 text-teal-400" />{m}</span>
                ))}
              </div>
              <div className="mt-5 flex items-center justify-between border-t border-[#DDDAD1] pt-4">
                <StarRating value={r.avg_overall} />
                <span className="text-xs text-slate-400">{r.review_count} reviews</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
